import { useEffect } from 'react'
import { useArenaStore } from '../store'

/**
 * First-visit onboarding line. Shown until the player's first real interaction
 * (an interaction dialog or a run inspector opening), then dismissed for good —
 * the dismissal is persisted to localStorage via the store, so a returning
 * player never sees it again.
 */
export function OnboardingHint() {
  const isPlaying           = useArenaStore((s) => s.isPlaying)
  const isPaused            = useArenaStore((s) => s.isPaused)
  const activeInteraction   = useArenaStore((s) => s.activeInteraction)
  const inspectorRunId      = useArenaStore((s) => s.inspectorRunId)
  const onboardingDismissed = useArenaStore((s) => s.onboardingDismissed)
  const dismissOnboarding   = useArenaStore((s) => s.dismissOnboarding)
  const playerZ             = useArenaStore((s) => s.playerZ)

  // The first interaction the player has — opening a dialog or the inspector —
  // permanently retires the hint.
  useEffect(() => {
    if (activeInteraction || inspectorRunId) dismissOnboarding()
  }, [activeInteraction, inspectorRunId, dismissOnboarding])

  if (onboardingDismissed) return null
  // Coordinate-island rooms are parked south of Z -1000. The outdoor prompt
  // must not cover interior work surfaces or tell somebody already inside to
  // walk up to a building.
  const insideBuilding = playerZ < -500
  if (!isPlaying || isPaused || activeInteraction || inspectorRunId || insideBuilding) return null

  return (
    <div style={s.wrap} data-testid="onboarding-hint">
      Explore <span style={s.accent}>Animus Quarter</span> — walk up to a building and press{' '}
      <span style={s.keycap}>E</span>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    position: 'fixed',
    bottom: '5.2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.45rem 1rem',
    background: 'rgba(61,64,91,0.75)',
    border: '1px solid rgba(245,235,224,0.12)',
    borderRadius: '9999px',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'rgba(245,235,224,0.72)',
    zIndex: 14,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  accent: {
    fontWeight: 800,
    color: '#f5ebe0',
  },
  keycap: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '0.1rem 0.35rem',
    background: 'rgba(245,235,224,0.1)',
    border: '1px solid rgba(245,235,224,0.2)',
    borderBottom: '2px solid rgba(245,235,224,0.1)',
    borderRadius: '0.25rem',
    color: 'rgba(245,235,224,0.85)',
  },
}
